import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../utils/api";
import toast from "react-hot-toast";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import compressCINImage from "../../utils/compressCINImage";

const VERIFY_DEBOUNCE_MS = 700;

const verifyCinPhoto = async (file, name, cin) => {
    const form = new FormData();
    form.append("recto", file);
    form.append("name", name);
    form.append("cin", cin);
    const { data } = await axiosInstance.post("/badge/verify-cin", form, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
};

const inputCls =
    "w-full bg-white border-2 border-gray-200 text-gray-900 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition placeholder-gray-400";

const Field = ({ label, required, children }) => (
    <div>
        <label className="block text-gray-800 text-sm font-semibold mb-1.5">
            {label} {required && <span className="text-amber-500">*</span>}
        </label>
        {children}
    </div>
);


const PhotoTile = ({ label, file, inputId, onChange, onCamera }) => (
    <div className="flex flex-col gap-2">
        <label className="block text-gray-800 text-sm font-semibold">
            {label} <span className="text-amber-500">*</span>
        </label>
        <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/*"
            onChange={onChange}
            className="hidden"
            id={inputId}
        />
        <label
            htmlFor={inputId}
            className={`flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-xl cursor-pointer transition select-none ${file
                ? "border-emerald-400 bg-emerald-50"
                : "border-gray-300 bg-gray-50 hover:bg-amber-50 hover:border-amber-300"
                }`}
        >
            {file ? (
                <div className="text-center px-2">
                    <svg className="w-8 h-8 text-emerald-500 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-emerald-700 text-xs font-semibold truncate max-w-[120px]">{file.name}</p>
                    <p className="text-emerald-500 text-xs mt-0.5">Tap to change</p>
                </div>
            ) : (
                <div className="text-center">
                    <svg className="w-8 h-8 text-gray-400 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <p className="text-gray-500 text-xs font-medium">Upload file</p>
                </div>
            )}
        </label>
        <button
            type="button"
            onClick={onCamera}
            className="flex items-center justify-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-semibold px-3 py-2 rounded-lg transition"
        >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Take Photo
        </button>
    </div>
);

const VerificationStatus = ({ verification, canRecheck, onRecheck }) => {
    const { status, cinMatch, nameMatch } = verification;

    let wrapperClass = "bg-slate-50 border-slate-200";
    let textClass = "text-slate-500";
    let text = "Waiting for verification...";
    let icon = (
        <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    );

    if (status === "checking") {
        wrapperClass = "bg-amber-50 border-amber-200";
        textClass = "text-amber-700";
        text = "Verifying CIN...";
        icon = <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin shrink-0" />;
    } else if (status === "error") {
        wrapperClass = "bg-orange-50 border-orange-200";
        textClass = "text-orange-700";
        text = "Verification unavailable — try again";
        icon = (
            <svg className="w-4 h-4 text-orange-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        );
    } else if (status === "done") {
        if (cinMatch && nameMatch) {
            wrapperClass = "bg-emerald-50 border-emerald-200";
            textClass = "text-emerald-700";
            text = "Info verified against photo";
            icon = (
                <svg className="w-4 h-4 text-emerald-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
            );
        } else {
            wrapperClass = "bg-red-50 border-red-200";
            textClass = "text-red-600";
            text = !cinMatch && !nameMatch
                ? "Name and CIN don't match the photo"
                : !cinMatch
                    ? "CIN doesn't match the photo"
                    : "Name doesn't match the photo";
            icon = (
                <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            );
        }
    }

    return (
        <div className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl border transition-colors duration-300 ${wrapperClass}`}>
            <div className="flex items-center gap-2 min-w-0">
                {icon}
                <span className={`text-xs font-medium truncate ${textClass}`}>{text}</span>
            </div>
            {canRecheck && (
                <button type="button" onClick={onRecheck} className="shrink-0 text-xs font-semibold text-amber-600 hover:text-amber-700 transition px-2 py-1 rounded-lg hover:bg-white/60">
                    Recheck
                </button>
            )}
        </div>
    );
};

const Steps = ({ current }) => {
    const steps = ["Company Info", "Fill Details"];

    return (
        <div className="flex items-center justify-center gap-0 mb-8">
            {steps.map((label, idx) => {
                const num = idx + 1;
                const active = num === current;
                const done = num < current;
                return (
                    <React.Fragment key={num}>
                        <div className="flex flex-col items-center">
                            <div
                                className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm border-2 transition-all ${done
                                    ? "bg-amber-500 border-amber-500 text-white"
                                    : active
                                        ? "bg-white border-amber-500 text-amber-600"
                                        : "bg-white border-gray-300 text-gray-400"
                                    }`}
                            >
                                {done ? (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : (
                                    num
                                )}
                            </div>
                            <span className={`text-xs mt-1.5 font-medium ${active ? "text-amber-600" : "text-gray-400"}`}>
                                {label}
                            </span>
                        </div>
                        {idx < steps.length - 1 && (
                            <div
                                className={`w-12 sm:w-20 h-0.5 mb-5 mx-1 transition-all ${num < current ? "bg-amber-500" : "bg-gray-200"
                                    }`}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

const PersonForm = ({ label, data, onChange, onPhoto, onCamera, personType, index, verification, canRecheck, onRecheck }) => (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-slate-800 px-6 py-4 flex items-center justify-between">
            <h3 className="text-white font-bold text-base flex items-center gap-2">
                <span className="bg-amber-400 text-slate-900 text-xs font-bold px-2 py-0.5 rounded-full">
                    Team Manager
                </span>
                {label}
            </h3>
            <span className="text-slate-400 text-xs font-medium uppercase tracking-wide">Contractor</span>
        </div>

        <div className="p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                    <Field label="Full Name" required>
                        <input
                            type="text"
                            placeholder="Enter full name"
                            value={data.name}
                            onChange={(e) => onChange("name", e.target.value)}
                            className={inputCls}
                        />
                    </Field>
                </div>
                <Field label="CIN" required>
                    <input
                        type="text"
                        placeholder="CIN number"
                        value={data.cin}
                        onChange={(e) => onChange("cin", e.target.value)}
                        className={inputCls}
                    />
                </Field>
                <Field label="Phone (WhatsApp)" required>
                    <input
                        type="tel"
                        placeholder="2126XXXXXXXX"
                        value={data.phone}
                        onChange={(e) => onChange("phone", e.target.value)}
                        className={inputCls}
                    />
                </Field>
            </div>

            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 space-y-4">
                <p className="text-slate-700 text-sm font-bold flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    </svg>
                    CIN Photos
                    <span className="text-amber-500 font-normal text-xs">— both required</span>
                </p>

                {verification && (
                    <VerificationStatus verification={verification} canRecheck={canRecheck} onRecheck={onRecheck} />
                )}

                <div className="grid grid-cols-2 gap-4">
                    <PhotoTile
                        label="Front (Recto)"
                        file={data.cinPhotos.recto}
                        inputId={`${personType}-${index ?? "m"}-recto`}
                        onChange={(e) => onPhoto("recto", e.target.files[0])}
                        onCamera={() => onCamera({ personType, index: index ?? null, side: "recto" })}
                    />
                    <PhotoTile
                        label="Back (Verso)"
                        file={data.cinPhotos.verso}
                        inputId={`${personType}-${index ?? "m"}-verso`}
                        onChange={(e) => onPhoto("verso", e.target.files[0])}
                        onCamera={() => onCamera({ personType, index: index ?? null, side: "verso" })}
                    />
                </div>
            </div>
        </div>
    </div>
);

/* ═══════════════════════════ ContractorForm ════════════════════════════ */

const ContractorForm = () => {
    const { eventId } = useParams();

    const [loading] = useState(false);
    const [error] = useState(null);

    const [mode, setMode] = useState(null); // null | "new"
    const [step, setStep] = useState(1);

    const [submitting, setSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const emptyPerson = () => ({
        name: "",
        phone: "",
        cin: "",
        cinPhotos: { recto: null, verso: null },
        verification: { status: "idle", cinMatch: null, nameMatch: null },
    });

    const [companyInfo, setCompanyInfo] = useState({
        companyName: "",
        chiefName: "",
        phone: "",
        email: "",
    });


    const [stream, setStream] = useState(null);
    const [cameraTarget, setCameraTarget] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [teamManager, setTeamManager] = useState(emptyPerson());

    const managerRef = useRef(teamManager);
    useEffect(() => { managerRef.current = teamManager; }, [teamManager]);

    const verifyTimer = useRef(null);
    useEffect(() => () => clearTimeout(verifyTimer.current), []);
    const verifySeq = useRef(0);

    const runVerification = useCallback(async (file, name, cin) => {
        if (!file || !name || !cin) return;
        const mySeq = ++verifySeq.current;

        setTeamManager((prev) => ({ ...prev, verification: { status: "checking", cinMatch: null, nameMatch: null } }));
        try {
            const result = await verifyCinPhoto(file, name, cin);
            if (mySeq !== verifySeq.current) return;
            setTeamManager((prev) => ({
                ...prev,
                verification: { status: "done", cinMatch: result.cinMatch, nameMatch: result.nameMatch },
            }));
        } catch {
            if (mySeq !== verifySeq.current) return;
            setTeamManager((prev) => ({ ...prev, verification: { status: "error", cinMatch: null, nameMatch: null } }));
            toast.error("CIN verification failed");
        }
    }, []);

    const scheduleVerification = useCallback(() => {
        clearTimeout(verifyTimer.current);
        verifyTimer.current = setTimeout(() => {
            const { name, cin, cinPhotos } = managerRef.current;
            if (cinPhotos.recto && name.trim() && cin.trim()) {
                runVerification(cinPhotos.recto, name.trim(), cin.trim());
            }
        }, VERIFY_DEBOUNCE_MS);
    }, [runVerification]);

    const handleRecheck = useCallback(() => {
        clearTimeout(verifyTimer.current);
        const { name, cin, cinPhotos } = managerRef.current;
        if (cinPhotos.recto && name.trim() && cin.trim()) {
            runVerification(cinPhotos.recto, name.trim(), cin.trim());
        }
    }, [runVerification]);

    const resetAll = () => {
        setMode(null);
        setStep(1);
        setTeamManager(emptyPerson());
        setCompanyInfo({ companyName: "", chiefName: "", phone: "", email: "" });
    };

    const handleSelectMode = (chosen) => {
        setMode(chosen);
        setStep(1);
    };

    /* ─────────── flow steps ─────────── */

    const handleStep1Next = () => {
        if (!companyInfo.companyName || !companyInfo.chiefName || !companyInfo.phone || !companyInfo.email) {
            toast.error("Please fill all company information.");
            return;
        }

        setTeamManager((prev) => ({
            ...prev,
            name: companyInfo.chiefName || "",
            phone: companyInfo.phone || "",
            email: companyInfo.email || "",
        }));

        setStep(2);
    };

    // keep manager pre-fill in sync if company info changes before step 2
    useEffect(() => {
        if (step === 2 && mode === "new") {
            setTeamManager((prev) => ({
                ...prev,
                name: companyInfo.chiefName,
                phone: companyInfo.phone,
                email: companyInfo.email,
            }));
        }
    }, [companyInfo, step, mode]);

    const handleManagerChange = (field, value) => {
        setTeamManager((prev) => ({
            ...prev,
            [field]: value,
            verification: (field === "name" || field === "cin")
                ? { status: "idle", cinMatch: null, nameMatch: null }
                : prev.verification,
        }));
        if (field === "name" || field === "cin") scheduleVerification();
    };

    const handleManagerPhoto = async (side, file) => {
        if (!file) return;
        try {
            const compressed = await compressCINImage(file);
            setTeamManager((prev) => ({
                ...prev,
                cinPhotos: { ...prev.cinPhotos, [side]: compressed },
                verification: side === "recto"
                    ? { status: "idle", cinMatch: null, nameMatch: null }
                    : prev.verification,
            }));
            if (side === "recto") scheduleVerification();
        } catch {
            toast.error("Could not process image. Use JPEG or PNG.");
        }
    };


    const startCamera = async (target) => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
            });
            setStream(mediaStream);
            setCameraTarget(target);
            setTimeout(() => {
                if (videoRef.current) videoRef.current.srcObject = mediaStream;
            }, 100);
        } catch {
            toast.error("Camera access denied or not available");
        }
    };

    const capturePhoto = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const track = stream.getVideoTracks()[0];
        const settings = track.getSettings();
        const maxSide = 1200;
        let w = settings.width || 1920;
        let h = settings.height || 1080;
        const scale = Math.min(maxSide / w, maxSide / h, 1);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(video, 0, 0, w, h);
        canvas.toBlob(
            (blob) => {
                const file = new File([blob], `cin-${cameraTarget.side}-${Date.now()}.jpg`, { type: "image/jpeg" });
                handleManagerPhoto(cameraTarget.side, file);
                stopCamera();
            },
            "image/jpeg",
            0.85
        );
    };

    const stopCamera = () => {
        if (stream) stream.getTracks().forEach((t) => t.stop());
        setStream(null);
        setCameraTarget(null);
    };

    /* ─────────── submit ─────────── */

    const handleSubmitNew = async () => {
        if (submitting) return;

        if (!teamManager.name || !teamManager.cin || !teamManager.phone) {
            toast.error("Please fill all required fields for the Team Manager.");
            return;
        }
        if (!teamManager.cinPhotos.recto || !teamManager.cinPhotos.verso) {
            toast.error("Please upload both CIN photos for the Team Manager.");
            return;
        }
        if (teamManager.verification.status === "checking") {
            toast.error("CIN verification in progress, please wait...");
            return;
        }
        if (teamManager.verification.status !== "done" || !teamManager.verification.cinMatch || !teamManager.verification.nameMatch) {
            toast.error("CIN verification not confirmed — please correct the info or retry.");
            return;
        }

        try {
            setSubmitting(true);

            const managerForm = new FormData();
            managerForm.append("name", teamManager.name);
            managerForm.append("email", teamManager.email || companyInfo.email);
            managerForm.append("phone", teamManager.phone);
            managerForm.append("companyName", companyInfo.companyName);
            managerForm.append("cin", teamManager.cin);
            managerForm.append("role", "contractor");
            managerForm.append("event", eventId);
            managerForm.append("cinPhotos", teamManager.cinPhotos.recto);
            managerForm.append("cinPhotos", teamManager.cinPhotos.verso);

            const managerRes = await axiosInstance.post("/auth/register", managerForm, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const managerId = managerRes.data.user._id;

            await axiosInstance.post("/badge", {
                standisteId: managerId,
                type: "contractor",
            });

            setShowSuccess(true);
            resetAll();
        } catch (err) {
            console.error("Submit error:", err.response?.status, err.response?.data);
            const isTimeout = err.code === "ECONNABORTED" || err.message?.includes("timeout");
            const isNetwork = err.message === "Network Error" || !err.response;
            toast.error(
                isTimeout || isNetwork
                    ? "Request took too long or connection was lost. Please check your connection and try again."
                    : err.response?.data?.message || err.response?.data?.error || err.message || "Submission failed."
            );
        } finally {
            setSubmitting(false);
        }
    };

    /* ─────────────────────────── render helpers ─────────────────────────── */

    if (loading) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-14 h-14 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Loading…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
                <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 max-w-sm text-center">
                    <svg className="w-10 h-10 text-red-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-800 font-semibold">{error}</p>
                </div>
            </div>
        );
    }

    /* ═════════════════════════════ JSX ══════════════════════════════════ */

    return (
        <div className="min-h-screen bg-stone-50 py-8 px-4 sm:px-6">
            <div className="max-w-2xl mx-auto">

                {/* ── header ── */}
                <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-slate-800 rounded-2xl mb-3 shadow-md">
                        <svg className="w-7 h-7 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Contractor Badge Registration</h1>
                    <p className="text-gray-500 text-sm mt-1">Register your team and submit your contractor badge</p>
                </div>

                {/* ── mode selector ── */}
                {mode === null && (
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="bg-slate-800 px-6 py-5 text-center">
                            <h2 className="text-white font-bold text-lg">Register your team</h2>
                            <p className="text-slate-400 text-sm mt-1">Enter your details and submit your badge</p>
                        </div>
                        <div className="p-6 flex justify-center">
                            <div className="max-w-md w-full">
                                <button
                                    onClick={() => handleSelectMode("new")}
                                    className="group flex flex-col items-center gap-4 p-6 rounded-2xl border-2 border-gray-200 hover:border-amber-500 hover:bg-amber-50 transition text-left w-full min-h-[280px]"
                                >
                                    <div className="w-14 h-14 bg-amber-100 group-hover:bg-amber-500 rounded-2xl flex items-center justify-center transition">
                                        <svg className="w-7 h-7 text-amber-600 group-hover:text-white transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                        </svg>
                                    </div>
                                    <div className="text-center flex-1 flex flex-col">
                                        <p className="font-bold text-gray-900 text-base group-hover:text-amber-600 transition">New Registration</p>
                                        <p className="text-gray-500 text-xs mt-1 leading-relaxed flex-1">
                                            Enter your company details and register your team manager.
                                        </p>
                                    </div>
                                    <span className="mt-auto inline-flex items-center gap-1 text-xs font-semibold text-amber-600">
                                        Get started
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                        </svg>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ════════════ NEW MODE ════════════ */}
                {mode === "new" && (
                    <>
                        <Steps current={step} />

                        {/* Step 1 — company info */}
                        {step === 1 && (
                            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="bg-slate-800 px-6 py-5">
                                    <h2 className="text-white font-bold text-lg">Company Information</h2>
                                    <p className="text-slate-400 text-sm mt-0.5">Enter your company and on-site contact details</p>
                                </div>
                                <div className="p-6 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <Field label="Company Name" required>
                                                <input
                                                    type="text"
                                                    value={companyInfo.companyName}
                                                    placeholder="e.g. Acme Construction Ltd."
                                                    onChange={(e) => setCompanyInfo((p) => ({ ...p, companyName: e.target.value }))}
                                                    className={inputCls}
                                                />
                                            </Field>
                                        </div>
                                        <div className="md:col-span-2">
                                            <Field label="Team Manager Name" required>
                                                <input
                                                    type="text"
                                                    value={companyInfo.chiefName}
                                                    placeholder="Full name of on-site manager"
                                                    onChange={(e) => setCompanyInfo((p) => ({ ...p, chiefName: e.target.value }))}
                                                    className={inputCls}
                                                />
                                            </Field>
                                        </div>
                                        <Field label="Phone (WhatsApp)" required>
                                            <PhoneInput
                                                international
                                                defaultCountry="MA"
                                                value={companyInfo.phone}
                                                onChange={(value) => setCompanyInfo((p) => ({ ...p, phone: value }))}
                                                className={inputCls}
                                            />
                                        </Field>
                                        <Field label="Email" required>
                                            <input
                                                type="email"
                                                value={companyInfo.email}
                                                placeholder="manager@company.com"
                                                onChange={(e) => setCompanyInfo((p) => ({ ...p, email: e.target.value }))}
                                                className={inputCls}
                                            />
                                        </Field>
                                    </div>

                                    <div className="flex gap-3 pt-2">
                                        <button
                                            onClick={resetAll}
                                            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-3.5 rounded-xl transition flex items-center justify-center gap-2"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                                            </svg>
                                            Back
                                        </button>
                                        <button
                                            onClick={handleStep1Next}
                                            className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold py-3.5 rounded-xl transition shadow flex items-center justify-center gap-2"
                                        >
                                            Continue
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 2 — fill manager details */}
                        {step === 2 && (
                            <div className="space-y-5">
                                <PersonForm
                                    data={teamManager}
                                    label="Team Manager"
                                    onChange={handleManagerChange}
                                    onPhoto={handleManagerPhoto}
                                    onCamera={startCamera}
                                    personType="teamManager"
                                    index={null}
                                    verification={
                                        teamManager.cinPhotos.recto && teamManager.name.trim() && teamManager.cin.trim()
                                            ? teamManager.verification
                                            : null
                                    }
                                    canRecheck={
                                        !!teamManager.cinPhotos.recto &&
                                        !!teamManager.name.trim() &&
                                        !!teamManager.cin.trim() &&
                                        teamManager.verification.status !== "checking"
                                    }
                                    onRecheck={handleRecheck}
                                />
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <button
                                            onClick={() => setStep(1)}
                                            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-3.5 rounded-xl transition flex items-center justify-center gap-2"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                                            </svg>
                                            Back to Setup
                                        </button>
                                        <button
                                            onClick={handleSubmitNew}
                                            disabled={submitting}
                                            className={`flex-1 font-bold py-3.5 rounded-xl transition shadow-md flex items-center justify-center gap-2 ${submitting ? "bg-emerald-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"
                                                } text-white`}
                                        >
                                            {submitting ? (
                                                <>
                                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    Submitting...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    Submit Badge
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ── camera overlay ── */}
            {stream && (
                <div className="fixed inset-0 bg-black/90 flex flex-col items-center justify-center z-50 p-4">
                    <div className="w-full max-w-lg">
                        <div className="bg-white rounded-t-2xl px-6 py-4 text-center">
                            <h3 className="font-bold text-gray-900">
                                Capture CIN — {cameraTarget?.side === "recto" ? "Front" : "Back"}
                            </h3>
                        </div>
                        <video ref={videoRef} autoPlay playsInline className="w-full bg-black" style={{ maxHeight: "55vh" }} />
                        <canvas ref={canvasRef} className="hidden" />
                        <div className="bg-white rounded-b-2xl px-6 py-5 flex gap-4">
                            <button
                                onClick={capturePhoto}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 rounded-xl transition flex items-center justify-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                </svg>
                                Capture
                            </button>
                            <button
                                onClick={stopCamera}
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3.5 rounded-xl transition flex items-center justify-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── success modal ── */}
            {showSuccess && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
                        <div className="bg-slate-800 px-6 py-8 flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-amber-400 rounded-full flex items-center justify-center mb-4 shadow-lg">
                                <svg className="w-9 h-9 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h2 className="text-white text-2xl font-bold">Submitted!</h2>
                            <p className="text-slate-400 text-sm mt-2">Your contractor badge has been successfully registered.</p>
                        </div>
                        <div className="px-6 py-5 space-y-3 text-center">
                            <p className="text-gray-500 text-sm">
                                Your submission is under review. Once we finish, you will receive your credentials by email to manage your staff/team and badges via our platform.
                            </p>
                            <button
                                onClick={() => setShowSuccess(false)}
                                className="w-full bg-slate-800 hover:bg-slate-700 text-white font-bold py-3 rounded-xl transition shadow"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContractorForm;